import { useEffect, useMemo, useRef, useState } from "react";
import { CaretDown, X } from "@phosphor-icons/react";

/**
 * Searchable combobox.
 *
 * Props:
 *  - value: string currently-selected value
 *  - onChange: (newValue:string) => void
 *  - options: Array<string | {value, label}>
 *  - placeholder: string
 *  - testid: string (added as data-testid on the input)
 *  - disabled: boolean
 *  - allowClear: boolean (default true)
 *  - emptyLabel: shown when no options match the query
 */
export default function SearchableSelect({
  value,
  onChange,
  options = [],
  placeholder = "Cari atau pilih...",
  testid,
  disabled = false,
  allowClear = true,
  emptyLabel = "Tidak ada hasil",
}) {
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);

  const normalized = useMemo(
    () =>
      options.map((o) =>
        typeof o === "string" ? { value: o, label: o } : o,
      ),
    [options],
  );

  // Reflect external value as the input's text when not searching
  const selectedLabel =
    normalized.find((o) => o.value === value)?.label || "";

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  // Click outside to close
  useEffect(() => {
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return normalized;
    const q = query.trim().toLowerCase();
    return normalized.filter((o) => o.label.toLowerCase().includes(q));
  }, [normalized, query]);

  const select = (opt) => {
    onChange(opt.value);
    setOpen(false);
    setQuery("");
    inputRef.current?.blur();
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActiveIdx((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      if (open && filtered[activeIdx]) {
        e.preventDefault();
        select(filtered[activeIdx]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const displayValue = open ? query : selectedLabel;

  return (
    <div ref={wrapperRef} className="relative">
      <div className="flex items-center gap-1">
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setActiveIdx(0);
          }}
          onFocus={() => {
            setOpen(true);
            setActiveIdx(0);
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full bg-white border border-slate-300 text-slate-900 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#0A3F9A] focus:border-[#0A3F9A] pr-16 disabled:bg-slate-50 disabled:cursor-not-allowed"
          data-testid={testid}
          autoComplete="off"
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {allowClear && value && !disabled && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange("");
                setQuery("");
                setOpen(false);
              }}
              className="text-slate-400 hover:text-slate-700 p-0.5"
              data-testid={testid ? `${testid}-clear` : undefined}
              tabIndex={-1}
            >
              <X size={14} weight="bold" />
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              if (!disabled) {
                setOpen((o) => !o);
                inputRef.current?.focus();
              }
            }}
            className="text-slate-500 hover:text-slate-800 p-0.5"
            tabIndex={-1}
          >
            <CaretDown
              size={14}
              weight="bold"
              className={`transition-transform ${open ? "rotate-180" : ""}`}
            />
          </button>
        </div>
      </div>
      {open && !disabled && (
        <div
          className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded shadow-lg max-h-64 overflow-y-auto"
          data-testid={testid ? `${testid}-list` : undefined}
        >
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-slate-400">{emptyLabel}</div>
          ) : (
            filtered.map((opt, i) => (
              <button
                type="button"
                key={`${opt.value}-${i}`}
                onClick={() => select(opt)}
                onMouseEnter={() => setActiveIdx(i)}
                className={`w-full text-left px-3 py-2 text-sm border-b last:border-b-0 border-slate-50 hover:bg-slate-100 transition-colors ${
                  i === activeIdx ? "bg-slate-100" : ""
                } ${value === opt.value ? "font-bold text-[#0A3F9A]" : "text-slate-800"}`}
                data-testid={
                  testid ? `${testid}-option-${i}` : undefined
                }
              >
                {opt.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
